import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Search, Filter, SortAsc, X, Loader2, Sliders, Package, Share2, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { fireEvent } from '@/lib/analytics';
import { DomainRow, dedupeByDomain, norm } from '@/lib/dedupe';
import {
  deriveBundleOptions,
  deriveTldOptions,
  deriveTagOptions,
  splitTags,
} from '@/lib/deriveOptions';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { breadcrumbJsonLd } from '@/lib/structuredData';
import AddToCartButton from '@/components/cart/AddToCartButton';
import FavButtonWithCount from './FavButtonWithCount';
import { useFavorites } from '@/hooks/useFavorites';
import { fuzzyScore, highlightQuery } from '@/lib/fuzzySearch';
import { getBundleCount } from '@/lib/bundles';
import SearchChips from './SearchChips';
import { search as relevanceSearch, getSearchStats } from '@/lib/search';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';

// Lightweight, optimized parallax engine scoped to this file
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}

function onParallaxScroll() {
  requestParallaxTick();
}

function onParallaxResize() {
  requestParallaxTick();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}

function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}

function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }

  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;

  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();

    if (rect.bottom < farTop || rect.top > farBottom) return;

    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;

    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;

    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);

    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }

    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

// Tag categories for filtering
const TAG_CATEGORIES = {
  industry: [
    'Accounting',
    'Legal',
    'Marketing',
    'HR',
    'IT',
    'Design',
    'Analytics',
    'E-commerce',
    'Nonprofit',
    'Healthcare',
    'Real Estate',
  ],
  useCase: [
    'Lead Gen',
    'SaaS',
    'Agency',
    'Marketplace',
    'Directory',
    'Education',
    'Tools',
    'Content',
    'Community',
  ],
  valueAdd: ['Premium', 'Keywords', 'Short', 'Two-Word'],
};

const ALL_TAGS = [
  ...TAG_CATEGORIES.industry,
  ...TAG_CATEGORIES.useCase,
  ...TAG_CATEGORIES.valueAdd,
];

// Sort options
const SORT_OPTIONS = [
  { value: 'price-high', label: 'Price (High→Low)' },
  { value: 'price-low', label: 'Price (Low→High)' },
  { value: 'a-z', label: 'A→Z' },
  { value: 'z-a', label: 'Z→A' },
  { value: 'length', label: 'Length (Short→Long)' },
  { value: 'relevance', label: 'Search Relevance' },
];

// Common TLDs for filtering
const COMMON_TLDS = ['com', 'net', 'org', 'io', 'ai', 'co', 'app', 'tech', 'dev', 'agency'];

export default function DomainsExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [raw, setRaw] = useState<DomainRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoggedInitialView = useRef(false);

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs (lightweight: title/subtitle + results grid container only)
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const resultsGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });

  // UI state for filters
  const ui = useMemo(
    () => ({
      q: searchParams.get('q') || '',
      bin: searchParams.get('availability_bin') === 'true',
      offer: searchParams.get('availability_offer') === 'true',
      rto: searchParams.get('availability_rto') === 'true',
      tags: searchParams.get('tags')?.split(',').filter(Boolean) || [],
      bundle: searchParams.get('bundle') || '',
      tld: searchParams.get('tld') || '',
      sort: searchParams.get('sort') || 'price-high',
      maxLen: searchParams.get('maxLength') ? parseInt(searchParams.get('maxLength')!) : undefined,
      saved: searchParams.get('saved') === 'true',
    }),
    [searchParams],
  );

  // State for debounced search
  const [searchInput, setSearchInput] = useState(ui.q);
  const [lengthRange, setLengthRange] = useState([1, ui.maxLen || 50]);
  const [searchResults, setSearchResults] = useState<DomainRow[]>([]);
  const [bundleCount, setBundleCount] = useState(0);
  const [shareClicked, setShareClicked] = useState(false);
  const { favorites, isFavorited, toggle: toggleFavorite } = useFavorites();

  const favoriteDomains = useMemo(() => new Set(favorites), [favorites]);

  const getInitialFavoritesCount = useCallback((slug: string) => {
    const sum = Array.from(slug).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (sum % 19) + 1;
  }, []);

  // Track bundle changes
  useEffect(() => {
    const updateBundleCount = () => {
      setBundleCount(getBundleCount());
    };

    updateBundleCount();
    window.addEventListener('bundle-updated', updateBundleCount);
    return () => window.removeEventListener('bundle-updated', updateBundleCount);
  }, []);

  // Get current filters from URL
  const selectedTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];
  const sortBy = searchParams.get('sort') || 'price-high';
  const selectedTLD = searchParams.get('tld') || '';
  const selectedBundle = searchParams.get('bundle') || '';
  const availabilityBin = searchParams.get('availability_bin') === 'true';
  const availabilityOffer = searchParams.get('availability_offer') === 'true';
  const availabilityRTO = searchParams.get('availability_rto') === 'true';
  const showSaved = searchParams.get('saved') === 'true';

  // Single fetch with AbortController
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await supabase
          .from('domains')
          .select(
            'domain, bin_price, tags, bundle, availability_bin, availability_offer, availability_rto, primary_keyword, domain_is_live, tld, length, updated_at',
          )
          .eq('domain_is_live', true)
          .eq('owned', true)
          .order('domain')
          .range(0, 999);

        if (!ac.signal.aborted) {
          const mapped = (data || []).map((d) => ({
            domain: d.domain,
            binPrice: d.bin_price,
            offer: d.availability_offer !== false,
            rto: d.availability_rto !== false,
            bundle: d.bundle,
            tld: d.tld,
            tags: d.tags,
            length: d.length,
            updated_at: d.updated_at,
          }));
          setRaw(mapped);
        }
      } catch (e: any) {
        if (!ac.signal.aborted) setError(e?.message ?? 'Load failed');
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const base = useMemo(() => dedupeByDomain(raw), [raw]);

  // Options are derived from BASE (not filtered) so dropdowns remain complete
  const bundleOptions = useMemo(() => deriveBundleOptions(base), [base]);
  const tldOptions = useMemo(() => deriveTldOptions(base), [base]);
  const tagOptions = useMemo(() => deriveTagOptions(base), [base]);

  // Filter logic (predictable; AND across groups, OR within group)
  function allowOffer(r: DomainRow) {
    return r.offer !== false;
  }
  function allowRto(r: DomainRow) {
    return r.rto !== false;
  }

  // Enhanced search with fuzzy matching and scoring
  const items = useMemo(() => {
    let rows = base;

    // Availability (bin/offer/rto)
    const wantsBIN = ui.bin;
    const wantsOffer = ui.offer;
    const wantsRTO = ui.rto;
    if (wantsBIN || wantsOffer || wantsRTO) {
      rows = rows.filter((r) => {
        const binOk = wantsBIN ? !!r.binPrice : true;
        const offOk = wantsOffer ? allowOffer(r) : true;
        const rtoOk = wantsRTO ? allowRto(r) : true;
        return binOk && offOk && rtoOk;
      });
    }

    // Bundle ('' means All)
    if (ui.bundle && ui.bundle !== '') {
      rows = rows.filter((r) => norm(r.bundle) === norm(ui.bundle));
    }

    // TLD ('' means All)
    if (ui.tld && ui.tld !== '') {
      rows = rows.filter((r) => norm(r.tld) === norm(ui.tld));
    }

    // Tags (OR within the single tag selection group)
    if (ui.tags.length) {
      rows = rows.filter((r) => {
        const rtags = splitTags(r.tags);
        const rset = new Set(rtags.map(norm));
        return ui.tags.some((t) => rset.has(norm(t)));
      });
    }

    // Length (inclusive)
    if (typeof ui.maxLen === 'number') {
      rows = rows.filter((r) => (r.length ?? norm(r.domain).length) <= ui.maxLen!);
    }

    // Saved filter - only show favorited domains
    if (ui.saved) {
      rows = rows.filter((r) => favoriteDomains.has(r.domain));
    }

    // Enhanced search with relevance engine (fuzzy + synonyms + boosts)
    if (ui.q && ui.q.trim()) {
      const query = ui.q.trim();
      const startTime = performance.now();

      const searchResults = relevanceSearch(rows, query, { minScore: 0 });
      const latency = performance.now() - startTime;

      // Track search analytics
      const stats = getSearchStats(searchResults, query, latency);
      fireEvent('search_executed', {
        qLen: stats.qLen,
        results: stats.results,
        latencyMs: stats.latencyMs,
        avgScore: stats.avgScore,
        sort: ui.sort,
      });

      const scoredRows = searchResults.map((result) => ({
        ...result.item,
        searchScore: result.score,
        domainHighlighted: result.highlighted,
      }));

      // Sort by search score if relevance mode
      if (ui.sort === 'relevance') {
        scoredRows.sort((a, b) => b.searchScore - a.searchScore);
      }

      rows = scoredRows;
    }

    // Sort (only if not already sorted by search relevance)
    if (!ui.q || ui.sort !== 'relevance') {
      if (ui.sort === 'price-low')
        rows = rows.slice().sort((a, b) => Number(a.binPrice || 0) - Number(b.binPrice || 0));
      else if (ui.sort === 'price-high')
        rows = rows.slice().sort((a, b) => Number(b.binPrice || 0) - Number(a.binPrice || 0));
      else if (ui.sort === 'a-z')
        rows = rows.slice().sort((a, b) => norm(a.domain).localeCompare(norm(b.domain)));
      else if (ui.sort === 'z-a')
        rows = rows.slice().sort((a, b) => norm(b.domain).localeCompare(norm(a.domain)));
      else if (ui.sort === 'length')
        rows = rows.slice().sort((a, b) => (a.length || 0) - (b.length || 0));
    }

    return rows;
  }, [base, ui, favoriteDomains]);

  // Analytics: Track initial page view
  useEffect(() => {
    if (!hasLoggedInitialView.current && !loading && base.length > 0) {
      fireEvent('domains_view', {
        query: ui.q || null,
        total: base.length,
      });
      hasLoggedInitialView.current = true;
    }
  }, [loading, base.length, ui.q]);

  // Enhanced debounced search effect (faster response)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (searchParams.get('q') || '')) {
        // Track search input analytics
        fireEvent('search_input', {
          qLength: searchInput.length,
          debouncedMs: 200,
        });
        updateURLParams({ q: searchInput || undefined });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [searchInput, searchParams]);

  // Track search results
  useEffect(() => {
    if (ui.q && items.length >= 0) {
      fireEvent('search_results', { count: items.length });
    }
  }, [items.length, ui.q]);

  // Length range effect
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentMin = parseInt(searchParams.get('minLength') || '1');
      const currentMax = parseInt(searchParams.get('maxLength') || '50');

      if (lengthRange[0] !== currentMin || lengthRange[1] !== currentMax) {
        updateURLParams({
          minLength: lengthRange[0] !== 1 ? lengthRange[0].toString() : undefined,
          maxLength: lengthRange[1] !== 50 ? lengthRange[1].toString() : undefined,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [lengthRange, searchParams]);

  const updateURLParams = (updates: Record<string, string | undefined>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams, { replace: true });

    // Analytics: Track filter changes
    Object.entries(updates).forEach(([key, value]) => {
      if (key === 'sort') {
        fireEvent('domains_sort', { key: 'sort', dir: value });
      } else {
        fireEvent('domains_filter', { key, value });
      }
    });
  };

  const toggleTag = (tag: string) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];

    // Analytics: Track tag changes specifically
    fireEvent('domains_filter', {
      key: 'tags',
      value: newTags.length > 0 ? newTags.join(',') : undefined,
      action: selectedTags.includes(tag) ? 'remove' : 'add',
      tag,
    });

    updateURLParams({
      tags: newTags.length > 0 ? newTags.join(',') : undefined,
    });
  };

  const toggleAvailability = (type: 'bin' | 'offer' | 'rto') => {
    const key = `availability_${type}`;
    const current = searchParams.get(key) === 'true';

    // Analytics: Track availability filter changes specifically
    fireEvent('domains_filter', {
      key,
      value: !current ? 'true' : undefined,
      availability_type: type,
    });

    updateURLParams({ [key]: !current ? 'true' : undefined });
  };

  const clearAllFilters = () => {
    fireEvent('search_clear_all');
    setSearchInput('');
    setLengthRange([1, 50]);
    setSearchParams(new URLSearchParams({ sort: 'price-high' }), { replace: true });
  };

  const handleShareLink = async () => {
    const url = window.location.href;

    try {
      if (navigator.share && /mobile/i.test(navigator.userAgent)) {
        await navigator.share({ url, title: 'Domain Search Results' });
        fireEvent('domains_share', { method: 'native' });
      } else {
        await navigator.clipboard.writeText(url);
        setShareClicked(true);
        setTimeout(() => setShareClicked(false), 2000);
        fireEvent('domains_share', { method: 'clipboard' });
      }
    } catch (err) {
      // Fallback: just show a toast or ignore
      console.error('Share failed:', err);
    }
  };

  // Quick filter chips data
  const chipGroups = useMemo(() => {
    const priceRanges = [
      { id: 'low', label: '$', min: 0, max: 2500 },
      { id: 'mid', label: '$$', min: 2501, max: 10000 },
      { id: 'high', label: '$$$', min: 10001, max: Infinity },
    ];

    const lengthRanges = [
      { id: 'short', label: '≤6', max: 6 },
      { id: 'medium', label: '7-10', min: 7, max: 10 },
      { id: 'long', label: '11-14', min: 11, max: 14 },
      { id: 'vlong', label: '15+', min: 15 },
    ];

    return [
      {
        id: 'price',
        label: 'Price Range',
        chips: priceRanges.map((range) => ({
          id: range.id,
          label: range.label,
          active: false, // TODO: Implement active state based on current filters
          count: base.filter((d) => {
            const price = d.binPrice || 0;
            return price >= range.min && price <= range.max;
          }).length,
        })),
      },
      {
        id: 'tld',
        label: 'Extension',
        chips: ['com', 'net', 'io', 'ai', 'co', 'app'].map((tld) => ({
          id: tld,
          label: `.${tld}`,
          active: ui.tld === tld,
          count: base.filter((d) => d.tld === tld).length,
        })),
      },
      {
        id: 'length',
        label: 'Name Length',
        chips: lengthRanges.map((range) => ({
          id: range.id,
          label: range.label,
          active: false, // TODO: Implement active state
          count: base.filter((d) => {
            const len = d.length || norm(d.domain).length;
            return range.min ? len >= range.min && len <= (range.max || 50) : len <= range.max;
          }).length,
        })),
      },
      {
        id: 'bundle',
        label: 'Bundle Status',
        chips: [
          {
            id: 'bundled',
            label: 'Has Bundle',
            active: false, // TODO: Implement active state
            count: base.filter((d) => d.bundle && d.bundle.trim()).length,
          },
          {
            id: 'standalone',
            label: 'Standalone',
            active: false, // TODO: Implement active state
            count: base.filter((d) => !d.bundle || !d.bundle.trim()).length,
          },
        ],
      },
    ];
  }, [base, ui]);

  const handleChipToggle = (groupId: string, chipId: string) => {
    fireEvent('search_chip_toggle', { chip: `${groupId}:${chipId}`, groupId, chipId });

    if (groupId === 'tld') {
      updateURLParams({ tld: ui.tld === chipId ? undefined : chipId });
    }
    // TODO: Add other chip toggle logic for price, length, bundle
  };

  const formatPrice = (price: number | undefined) => {
    if (!price) return 'Contact for price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getTagColor = (tag: string) => {
    if (TAG_CATEGORIES.industry.includes(tag)) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (TAG_CATEGORIES.useCase.includes(tag)) return 'bg-green-100 text-green-800 border-green-200';
    if (TAG_CATEGORIES.valueAdd.includes(tag))
      return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const hasActiveFilters = Boolean(
    ui.tags.length > 0 ||
      ui.q ||
      ui.tld ||
      ui.bundle ||
      ui.bin ||
      ui.offer ||
      ui.rto ||
      (ui.maxLen && ui.maxLen < 50) ||
      ui.saved,
  );

  // Track empty state views
  useEffect(() => {
    if (!loading && items.length === 0 && hasActiveFilters) {
      fireEvent('domains_empty_state_view', {
        filters_count: [
          ui.tags.length > 0,
          Boolean(ui.q),
          Boolean(ui.tld),
          Boolean(ui.bundle),
          ui.bin,
          ui.offer,
          ui.rto,
          Boolean(ui.maxLen && ui.maxLen < 50),
          ui.saved,
        ].filter(Boolean).length,
      });
    }
  }, [loading, items.length, hasActiveFilters, ui]);

  if (error) {
    return (
      <div className="min-h-screen liquid-mesh-bg py-20 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Unable to Load Domains</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={withTemplate('Browse all domains')}
        description="Filter by bundle, TLD, tags, or length. Buy now or make an offer with a trusted payment provider (I cover all fees)."
        canonical={canonicalize('/domains' + (location.search.replace(/^\?$/, '') || ''))}
        og={{
          title: 'Browse all domains',
          description: 'Find a brandable domain and buy via a trusted payment provider.',
          url: canonicalize('/domains'),
        }}
        twitter={{
          card: 'summary',
          title: 'Browse all domains',
          description: 'Find a brandable domain and buy via a trusted payment provider.',
        }}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: canonicalize('/') },
          { name: 'Domains', url: canonicalize('/domains') },
        ])}
      />

      <div className="min-h-screen py-20 relative overflow-hidden" style={backgroundStyle}>
        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Header */}
          <div className="text-center mb-16 stagger-fade-1">
            <h1 ref={heroTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              Find your{' '}
              <Link to="/domains" className="inline-block hover:opacity-90 transition-opacity">
                <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                  Domain
                </AnimatedGradientText>
              </Link>
            </h1>
            <p ref={heroSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
              Your journey begins here
            </p>
          </div>

          {/* Controls - Sticky on mobile */}
          <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border border-border rounded-3xl p-6 mb-12 stagger-fade-2">
            {/* Search Bar */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search domains or keywords... (supports typos)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-12 py-4 text-lg border-border/50 focus:border-primary"
                aria-describedby="search-help"
              />
              {searchInput && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <span className="text-sm text-muted-foreground">{items.length} results</span>
                </div>
              )}
            </div>

            {/* Quick Filter Chips */}
            <div className="mb-6">
              <SearchChips
                chipGroups={chipGroups}
                onChipToggle={handleChipToggle}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>

            {/* Filters Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Sort by
                </label>
                <select
                  value={ui.sort}
                  onChange={(e) => updateURLParams({ sort: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bundle */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Bundle
                </label>
                <select
                  value={ui.bundle}
                  onChange={(e) => updateURLParams({ bundle: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {bundleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* TLD */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">TLD</label>
                <select
                  value={ui.tld}
                  onChange={(e) => updateURLParams({ tld: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {tldOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value ? `.${option.label}` : option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Length Range */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Length: {lengthRange[0]}-{lengthRange[1]} chars
                </label>
                <Slider
                  value={lengthRange}
                  onValueChange={setLengthRange}
                  max={50}
                  min={1}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>

            {/* Availability Toggles */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-muted-foreground mb-3">
                Availability
              </label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="availability-bin"
                    checked={ui.bin}
                    onCheckedChange={() => toggleAvailability('bin')}
                  />
                  <label
                    htmlFor="availability-bin"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Buy It Now
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="availability-offer"
                    checked={ui.offer}
                    onCheckedChange={() => toggleAvailability('offer')}
                  />
                  <label
                    htmlFor="availability-offer"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Make Offer
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="availability-rto"
                    checked={ui.rto}
                    onCheckedChange={() => toggleAvailability('rto')}
                  />
                  <label
                    htmlFor="availability-rto"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Rent to Own
                  </label>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-muted-foreground mb-3">Tags</label>
              <div className="space-y-4">
                {Object.entries(TAG_CATEGORIES).map(([category, tags]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                      {category === 'useCase'
                        ? 'Use Case'
                        : category === 'valueAdd'
                          ? 'Value Add'
                          : category}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant={ui.tags.includes(tag) ? 'default' : 'outline'}
                          className="cursor-pointer transition-all hover:scale-105"
                          onClick={() => toggleTag(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div className="flex items-center gap-4">
                <p className="text-muted-foreground">
                  {loading
                    ? 'Loading...'
                    : `${items.length} domain${items.length !== 1 ? 's' : ''} found`}
                  {hasActiveFilters && !loading && (
                    <span className="ml-2 text-primary">with filters applied</span>
                  )}
                </p>

                {/* Bundle Chip */}
                {bundleCount > 0 && (
                  <Link to="/bundles/builder">
                    <Badge
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      <Package className="w-3 h-3 mr-1" />
                      Bundle ({bundleCount})
                    </Badge>
                  </Link>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Share Link Button */}
                <Button
                  onClick={handleShareLink}
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  title="Share this search"
                >
                  {shareClicked ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      Share
                    </>
                  )}
                </Button>

                {hasActiveFilters && (
                  <Button onClick={clearAllFilters} variant="outline" size="sm" className="gap-2">
                    <X className="w-4 h-4" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          {loading && items.length === 0 ? (
            /* Loading Skeleton */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {Array.from({ length: 12 }).map((_, i) => (
                <Card key={i} className="h-[300px]">
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-3/4 mb-4" />
                    <Skeleton className="h-4 w-1/2 mb-6" />
                    <Skeleton className="h-8 w-1/3 mb-4" />
                    <div className="flex gap-2 mb-4">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-6 w-14" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : items.length === 0 ? (
            /* Enhanced Empty State */
            <div className="text-center py-20 stagger-fade-3">
              <div className="w-24 h-24 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-12 h-12 text-muted-foreground/50" />
              </div>
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                No domains match your filters
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {ui.q ? (
                  <>
                    Try a different search term, or clear your filters to see all available domains.
                  </>
                ) : ui.maxLen && ui.maxLen < 20 ? (
                  <>Try increasing the length range, or clear filters to see more options.</>
                ) : ui.tags.length > 0 ? (
                  <>Try removing some tags, or clear all filters to broaden your search.</>
                ) : (
                  <>Try adjusting your search terms or filters to find more domains.</>
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button onClick={clearAllFilters} variant="default" size="lg">
                  <X className="w-4 h-4 mr-2" />
                  Clear All Filters
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/domains">View All Domains</Link>
                </Button>
              </div>
              {hasActiveFilters && (
                <p className="text-xs text-muted-foreground mt-6">
                  Active filters:{' '}
                  {[
                    ui.q && 'Search',
                    ui.bundle && 'Bundle',
                    ui.tld && 'TLD',
                    ui.tags.length > 0 && `${ui.tags.length} tag${ui.tags.length > 1 ? 's' : ''}`,
                    ui.maxLen && ui.maxLen < 50 && 'Length',
                    (ui.bin || ui.offer || ui.rto) && 'Availability',
                    ui.saved && 'Saved',
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Domain Grid */}
              <div ref={resultsGridRef as any} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {items.map((domain, index) => (
                  <div
                    key={domain.domain}
                    className={`group stagger-fade-${Math.min((index % 4) + 3, 6)}`}
                  >
                    <Link
                      to={`/domain/${domain.domain}`}
                      onClick={() => fireEvent('domain_click', { domain: domain.domain })}
                      className="block h-full"
                    >
                      <Card className="h-full transition-all duration-300 hover:scale-105 hover:shadow-xl border-border/50 hover:border-primary/20 relative">
                        <CardContent className="p-6">
                          {/* Favorite Toggle */}
                          <div
                            className="absolute top-3 right-3 z-10"
                            onClick={(e) => e.preventDefault()}
                          >
                            {(() => {
                              const saved = isFavorited(domain.domain);
                              const baseCount = getInitialFavoritesCount(domain.domain);
                              const displayCount = saved ? baseCount + 1 : baseCount;
                              return (
                                <FavButtonWithCount
                                  domain={domain.domain}
                                  favoritesCount={displayCount}
                                  isFavorited={saved}
                                  onToggle={() => toggleFavorite(domain.domain)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                />
                              );
                            })()}
                          </div>

                          {/* Domain Name */}
                          <h3
                            className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors"
                            dangerouslySetInnerHTML={{
                              __html: (domain as any).domainHighlighted || domain.domain,
                            }}
                          />

                          {/* Bundle */}
                          {domain.bundle && (
                            <p className="text-sm text-muted-foreground mb-4">
                              Bundle: {domain.bundle}
                            </p>
                          )}

                          {/* Price */}
                          <div className="text-3xl font-bold text-attain-orange mb-4">
                            {formatPrice(domain.binPrice)}
                          </div>

                          {/* Tags */}
                          {domain.tags && splitTags(domain.tags).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {splitTags(domain.tags)
                                .slice(0, 3)
                                .map((tag) => (
                                  <Badge
                                    key={tag}
                                    variant="outline"
                                    className={`text-xs ${getTagColor(tag)}`}
                                  >
                                    {tag}
                                  </Badge>
                                ))}
                              {splitTags(domain.tags).length > 3 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-gray-50 text-gray-600"
                                >
                                  +{splitTags(domain.tags).length - 3}
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Escrow message */}
                          <p className="text-xs text-muted-foreground mb-4">
                            I pay 100% of the payment provider fees.
                          </p>

                          {/* Availability indicators */}
                          <div className="flex gap-2 mb-4">
                            {domain.binPrice && (
                              <Badge variant="secondary" className="text-xs">
                                Buy Now
                              </Badge>
                            )}
                            {domain.offer && (
                              <Badge variant="outline" className="text-xs">
                                Offers
                              </Badge>
                            )}
                            {domain.rto && (
                              <Badge variant="outline" className="text-xs">
                                RTO
                              </Badge>
                            )}
                          </div>

                          {/* CTA Section */}
                          <div className="mt-4 pt-4 border-t border-border space-y-2">
                            {domain.binPrice && (
                              <AddToCartButton
                                domain={domain.domain}
                                tld={domain.tld}
                                binPrice={domain.binPrice}
                                location="list"
                                className="w-full"
                              />
                            )}
                            <Button asChild variant="outline" className="w-full">
                              <Link to={`/domain/${domain.domain}`}>View Details →</Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
